'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { Switch } from './switch';

/**
 * Light/dark theme primitives. Drop `<LightDarkProvider>` at the app root,
 * mount `<ThemeToggle />` anywhere inside (typically the sidebar footer),
 * and read state via `useTheme()` from any component.
 *
 * The provider toggles a `.dark` class on `<html>` from localStorage —
 * that's the hook `@loop3/ui`'s tokens use (see `tokens.css`'s
 * `@custom-variant dark (&:is(.dark, .dark *))`). Adding the class swaps
 * every CSS variable to its dark value.
 *
 * Storage key defaults to `loop3:theme` — note that localStorage is
 * per-origin, so subdomains (admin.loop3.ai, timeoff.loop3.ai, etc.) don't
 * share the value even with the same key. The shared key is for clarity,
 * not cross-app sync.
 */

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const DEFAULT_STORAGE_KEY = 'loop3:theme';

function readInitial(storageKey: string): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(storageKey);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
}

export interface LightDarkProviderProps {
  children: React.ReactNode;
  /** localStorage key. Defaults to `loop3:theme`. */
  storageKey?: string;
}

export function LightDarkProvider({
  children,
  storageKey = DEFAULT_STORAGE_KEY,
}: LightDarkProviderProps) {
  const [theme, setThemeState] = React.useState<Theme>(() => readInitial(storageKey));

  React.useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    window.localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: setThemeState,
      toggle: () => setThemeState((t) => (t === 'light' ? 'dark' : 'light')),
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <LightDarkProvider>');
  return ctx;
}

/**
 * Inline sun + moon icons — kept out of lucide-react so this package has no
 * icon-library dep (matches the `<GearIcon>` pattern in `account-widget.tsx`).
 */
function SunIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.95 3.05l-1.06 1.06M4.11 11.89l-1.06 1.06M12.95 12.95l-1.06-1.06M4.11 4.11L3.05 3.05" />
    </svg>
  );
}

function MoonIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M13.5 9.5A6 6 0 0 1 6.5 2.5a6 6 0 1 0 7 7Z" />
    </svg>
  );
}

export interface ThemeToggleProps {
  className?: string;
}

/**
 * Sun → Switch → Moon control. Reads + writes the active theme via
 * `useTheme()` — must sit inside a `<LightDarkProvider>`. The flanking
 * icons are clickable shortcuts; the switch reflects the active state.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <button
        type="button"
        aria-label="Switch to light mode"
        title="Light mode"
        onClick={() => setTheme('light')}
        className={cn(
          'inline-flex cursor-pointer items-center justify-center transition-colors',
          isDark ? 'text-muted-foreground/60 hover:text-foreground' : 'text-foreground',
        )}
      >
        <SunIcon className="size-3.5" />
      </button>
      <Switch
        checked={isDark}
        onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        className="h-5 w-9 [&>span]:size-4 [&>span[data-state=checked]]:translate-x-4"
      />
      <button
        type="button"
        aria-label="Switch to dark mode"
        title="Dark mode"
        onClick={() => setTheme('dark')}
        className={cn(
          'inline-flex cursor-pointer items-center justify-center transition-colors',
          isDark ? 'text-foreground' : 'text-muted-foreground/60 hover:text-foreground',
        )}
      >
        <MoonIcon className="size-3.5" />
      </button>
    </div>
  );
}
